import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./AddTrackerModal.module.scss";
import { withError } from "src/hocs";
import { Form, Input } from "src/components";
import { removeExtraSpaces } from "src/utils";
import { ConfirmModal } from "src/features/index";
import { TRACKER_INPUT_LIMIT } from "src/constants";
import { useModal, useTemporaryErrors } from "src/hooks";

const InputWithError = withError(Input);

type Props = {
  submitHandler: (value: Tracker.CreationData["name"]) => void;
};

export const AddTrackerModal: FC<Props> = ({ submitHandler }) => {
  const { t } = useTranslation();

  const { closeModal } = useModal();

  const { errors, setErrors } = useTemporaryErrors(3000);

  const [name, setName] = useState<Tracker.CreationData["name"]>("");

  const isDisabled = useMemo<boolean>(() => !name, [name]);

  const onSubmit = (): void => {
    if (isDisabled) return;

    const errors = validate();

    if (Object.keys(errors).length) return setErrors(errors);

    const formattedName = removeExtraSpaces(name);

    submitHandler(formattedName);

    closeModal("add-tracker");
  };

  function validate() {
    const validationErrors: typeof errors = {};

    if (!name.trim().length)
      validationErrors.name = t(
        "component.modal.add_tracker.form.validation.name_required",
      );

    return validationErrors;
  }

  return (
    <ConfirmModal
      id="add-tracker"
      type="success"
      size="medium"
      title={t("component.modal.add_tracker.title")}
      cancelButton={{
        onClick: () => closeModal("add-tracker"),
        text: t("component.modal.add_tracker.button.cancel"),
      }}
      acceptButton={{
        onClick: onSubmit,
        disabled: isDisabled,
        text: t("component.modal.add_tracker.button.submit"),
      }}
    >
      <Form
        onSubmit={onSubmit}
        disabled={isDisabled}
        className={styles.formWrapper}
      >
        <InputWithError
          autoFocus
          value={name}
          error={errors.name}
          changeHandler={setName}
          characterLimit={TRACKER_INPUT_LIMIT}
          placeholder={t("component.modal.add_tracker.form.placeholder.name")}
        />
      </Form>
    </ConfirmModal>
  );
};
